import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import SEO from '@/components/SEO';
import { Ruler, FileText, Upload, CreditCard, Download, CheckCircle, Image as ImageIcon } from 'lucide-react';

const HowItWorks = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO 
        pageId="how-it-works"
        defaultTitle="How It Works | House Plan Direct"
        defaultDescription="Simple 4-step process: Upload your sketch or old plan, we redraw it professionally, receive CAD and DWG files, use for planning or building. UK-wide service."
        keywords="sketch to CAD process, how to get floor plans redrawn, old plans converted to CAD, upload sketch get CAD"
      />
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Page Header */}
        <div className="text-center mb-12" data-testid="page-header">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">How It Works</h1>
          <p className="text-lg text-gray-600">
            Converting your sketches into professional CAD floor plans is simple and straightforward.
          </p>
        </div>

        {/* Cross-link to the free DIY toolkit — additive only, does not alter the managed-service steps below */}
        <div className="mb-12 rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-center" data-testid="diy-alternative-callout">
          <p className="text-sm text-gray-700">
            Prefer to do it yourself? Create, measure and present your own floor plan online — free to build and edit.
          </p>
          <Link to="/plan-measure">
            <Button variant="outline" size="sm" className="mt-3 border-emerald-600 text-emerald-700 hover:bg-emerald-100" data-testid="how-it-works-try-diy-btn">
              Start HPD Draw Free
            </Button>
          </Link>
        </div>

        {/* Step-by-Step Process */}
        <div className="space-y-12">
          {/* Step 1 */}
          <div className="bg-white rounded-xl shadow-md p-8 border border-gray-200" data-testid="step-measure">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                <Ruler className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900 mb-3">Step 1: Measure and Sketch Your Property</h2>
                <p className="text-gray-600 mb-4">
                  Use our comprehensive measuring guidance to accurately measure your property. We provide:
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Free measuring guide with every order</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Graph paper and measurement symbols</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Step-by-step instructions</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Tips for accurate measurements</span>
                  </li>
                </ul>
                <p className="text-gray-600 mt-4">
                  <strong>Important:</strong> You are responsible for all measurements. Please measure carefully to ensure accuracy.
                </p>
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div className="bg-white rounded-xl shadow-md p-8 border border-gray-200" data-testid="step-upload">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                <Upload className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900 mb-3">Step 2: Upload or Email Your Sketch</h2>
                <p className="text-gray-600 mb-4">
                  We accept sketches in various formats:
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <FileText className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Hand-drawn sketches (scanned or photographed)</span>
                  </li>
                  <li className="flex items-start">
                    <ImageIcon className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Photos of existing plans</span>
                  </li>
                  <li className="flex items-start">
                    <FileText className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Existing floor plans, elevations, or sections</span>
                  </li>
                  <li className="flex items-start">
                    <Upload className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Any image or document format</span>
                  </li>
                </ul>
                <p className="text-gray-600 mt-4">
                  Upload during checkout or email to <a href="mailto:web@houseplandirect.com" className="text-blue-600 hover:underline">web@houseplandirect.com</a> with your order number.
                </p>
              </div>
            </div>
          </div>

          {/* Step 3 */}
          <div className="bg-white rounded-xl shadow-md p-8 border border-gray-200" data-testid="step-pay">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                <CreditCard className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900 mb-3">Step 3: Choose Options and Pay Online</h2>
                <p className="text-gray-600 mb-4">
                  Customise your floor plan with optional extras:
                </p>
                <ul className="space-y-2 text-gray-600 mb-4">
                  <li className="flex justify-between">
                    <span>Add colour</span>
                    <span className="font-semibold">+£10</span>
                  </li>
                  <li className="flex justify-between">
                    <span>Add 3D visualisation</span>
                    <span className="font-semibold text-blue-600">Price on request</span>
                  </li>
                  <li className="flex justify-between">
                    <span>Add furniture</span>
                    <span className="font-semibold">+£25</span>
                  </li>
                  <li className="flex justify-between">
                    <span>Add symbols & key</span>
                    <span className="font-semibold">+£25</span>
                  </li>
                </ul>
                <p className="text-gray-600">
                  Pay securely online with fixed pricing. We accept PayPal for your convenience.
                </p>
              </div>
            </div>
          </div>

          {/* Step 4 */}
          <div className="bg-white rounded-xl shadow-md p-8 border border-gray-200" data-testid="step-receive">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                <Download className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900 mb-3">Step 4: Receive Your Professional CAD Floor Plan</h2>
                <p className="text-gray-600 mb-4">
                  You'll receive your completed floor plan by email in multiple formats:
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span><strong>PDF</strong> - Perfect for printing and sharing</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span><strong>JPEG</strong> - Great for presentations and websites</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span><strong>DWG</strong> - CAD format for further editing</span>
                  </li>
                </ul>
                <p className="text-gray-600 mt-4">
                  We will correct any errors in the conversion that are our responsibility at no extra charge.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Information */}
        <div className="mt-12 bg-blue-50 rounded-xl p-8 border border-blue-200" data-testid="additional-info">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Important Notes</h3>
          <ul className="space-y-3 text-gray-600">
            <li className="flex items-start">
              <span className="text-blue-600 font-bold mr-2">•</span>
              <span>Plans are drawn as presented. If you require a specific scale or page size, please mark this on your sketch.</span>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 font-bold mr-2">•</span>
              <span>Cupboards and corridors do not count towards the room count.</span>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 font-bold mr-2">•</span>
              <span>Any amendments required by you after delivery are charged at £25 per hour (minimum charge £10).</span>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 font-bold mr-2">•</span>
              <span>You can add your own logo and disclaimer by sending us the logo file and exact text.</span>
            </li>
          </ul>
        </div>

        {/* CTA */}
        <div className="text-center mt-12" data-testid="cta-section">
          <Link to="/order">
            <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-lg px-8" data-testid="start-order-btn">
              Start Your Order Now
            </Button>
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default HowItWorks;
